const normalize = (text) => String(text || '')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .toLowerCase()
  .replace(/[^a-z0-9\s']/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const tokenize = (text) => normalize(text).split(' ').filter(Boolean);

const GREETING_TIME_WORDS = new Set(['morning', 'afternoon', 'evening', 'night']);

const greetingRepliesFor = (text) => {
  if (/\b(good\s*night|gn|nighty)\b/.test(text) || text.includes('good night')) {
    return ['Good night', 'Sleep well', 'Take care'];
  }
  if (text.includes('good afternoon') || text.includes('afternoon')) {
    return ['Good afternoon', 'Hello', 'Hope you’re well'];
  }
  if (text.includes('good evening') || /\bevening\b/.test(text)) {
    return ['Good evening', 'Hello', 'Hope you’re well'];
  }
  if (
    text.includes('good morning')
    || text.includes('goodmorning')
    || /\bgm\b/.test(text)
    || /\bmorning\b/.test(text)
  ) {
    return ['Good morning', 'Hello', 'Have a great day'];
  }
  return ['Hello', 'Hi', 'Good morning'];
};

const REPLY_MATRIX = [
  {
    id: 'greeting',
    phrases: [
      'good morning',
      'good afternoon',
      'good evening',
      'good night',
      'good day',
      'hello',
      'hi there',
      'hey there',
      'namaste',
      'goodmorning',
    ],
    words: ['hello', 'hi', 'hey', 'gm', 'gn', 'namaste', 'hola', 'howdy'],
    prefixes: ['greet'],
    replies: ['Good morning', 'Hello', 'Hi'],
  },
  {
    id: 'call',
    phrases: [
      'have a call',
      'can we call',
      'jump on a call',
      'quick call',
      'want to discuss',
      'lets discuss',
      'let us discuss',
      'let s discuss',
      'lets talk',
      'let s talk',
      'catch up',
      'catchup',
    ],
    words: ['call', 'meeting', 'meet', 'discuss', 'discussion', 'zoom', 'huddle', 'sync', 'standup', 'hangout'],
    prefixes: ['discuss', 'meetin', 'schedul', 'avail', 'calend'],
    replies: ['Sure, let’s call', 'I’m free now', 'What time works?'],
  },
  {
    id: 'thanks',
    phrases: ['thank you', 'thanks a lot', 'thanks so much', 'much appreciated'],
    words: ['thanks', 'thank', 'thx', 'ty', 'appreciated', 'grateful'],
    prefixes: ['thank', 'appreciat'],
    replies: ['Thanks', 'Awesome, thanks', 'Anytime!'],
  },
  {
    id: 'help',
    phrases: ['can you help', 'need help', 'could you help', 'stuck on'],
    words: ['help', 'stuck', 'issue', 'problem', 'blocked', 'blocker', 'asap'],
    prefixes: ['issu', 'proble', 'block'],
    replies: ['On it', 'Let me check', 'Sure'],
  },
  {
    id: 'approval',
    phrases: ['please approve', 'can you review', 'looks good', 'lgtm', 'sign off'],
    words: ['approve', 'approval', 'review', 'confirm', 'confirmation', 'lgtm'],
    prefixes: ['approv', 'review', 'confirm'],
    replies: ['Looks good', 'Approved', 'I’ll review'],
  },
  {
    id: 'question',
    phrases: ['can we', 'could we', 'can you', 'could you', 'would you', 'is it possible'],
    words: ['please', 'pls'],
    prefixes: [],
    replies: ['Yes', 'Sure', 'Let me check'],
  },
  {
    id: 'time',
    phrases: ['what time', 'are you free', 'when works', 'this afternoon', 'tomorrow'],
    words: ['tomorrow', 'today', 'tonight', 'morning', 'afternoon', 'evening', 'later', 'soon'],
    prefixes: ['tomorr', 'aftern', 'mornin', 'evenin'],
    replies: ['Works for me', 'Later today?', 'Tomorrow?'],
  },
  {
    id: 'docs',
    phrases: ['please send', 'share the file', 'shared a file', 'please check the doc'],
    words: ['file', 'files', 'document', 'doc', 'docs', 'pdf', 'attachment', 'link'],
    prefixes: ['attach', 'docu'],
    replies: ['Sent', 'Checking now', 'Got it'],
  },
  {
    id: 'sorry',
    phrases: ['i am sorry', 'my bad', 'apologies'],
    words: ['sorry', 'apology', 'apologies'],
    prefixes: ['apolog'],
    replies: ['No worries', 'All good', 'Thanks for the update'],
  },
  {
    id: 'congrats',
    phrases: ['well done', 'great job', 'congrats'],
    words: ['congrats', 'congratulations', 'woohoo', 'celebrate'],
    prefixes: ['congrat'],
    replies: ['Congrats!', 'Awesome', 'Well done'],
  },
];

const scoreRule = (rule, text, tokens, hasQuestion) => {
  let score = 0;
  rule.phrases.forEach((phrase) => {
    if (text.includes(phrase)) score += 5;
  });
  tokens.forEach((token) => {
    if (rule.id === 'time' && GREETING_TIME_WORDS.has(token) && text.includes(`good ${token}`)) {
      return;
    }
    if (rule.words.includes(token)) score += 3;
    if (rule.prefixes.some((prefix) => prefix.length >= 4 && (token.startsWith(prefix) || (prefix.startsWith(token) && token.length >= 4)))) {
      score += 2;
    }
    if (rule.words.some((word) => word.length >= 4 && (token.includes(word) || (word.includes(token) && token.length >= 4)))) {
      score += 1;
    }
  });
  if (rule.id === 'question' && (hasQuestion || /^(can|could|would|will|shall|do|did|is|are)\b/.test(text))) {
    score += 4;
  }
  return score;
};

export const suggestedRepliesForText = (rawText, { limit = 3 } = {}) => {
  const source = String(rawText || '');
  const hasQuestion = /\?/.test(source);
  const text = normalize(source);
  if (!text || text.length < 3) return [];
  const tokens = tokenize(text);
  const ranked = REPLY_MATRIX
    .map((rule) => ({ rule, score: scoreRule(rule, text, tokens, hasQuestion) }))
    .filter((entry) => entry.score >= 3)
    .sort((a, b) => b.score - a.score);

  const replies = [];
  ranked.forEach(({ rule }) => {
    const options = rule.id === 'greeting' ? greetingRepliesFor(text) : rule.replies;
    options.forEach((reply) => {
      if (!replies.includes(reply) && replies.length < limit) {
        replies.push(reply);
      }
    });
  });
  return replies;
};

export const escapeReplyHtml = (text) => String(text || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

export const CALL_ME_BACK_PLEASE = 'Call me back please';
export const CALL_THIS_PERSON = 'Call this person';
export const WILL_CALL_IN_SOMETIME = 'Will call you in sometime';

export const isCallMeBackPlease = (rawText) => {
  const text = normalize(rawText);
  return text === 'call me back please' || text === 'call me back';
};

export const isWillCallInSometime = (rawText) => {
  const text = normalize(rawText).replace(/\bu\b/g, 'you');
  if (!text) return false;
  if (text === WILL_CALL_IN_SOMETIME.toLowerCase()) return true;
  if (text.includes('will call') && (text.includes('sometime') || text.includes('later'))) return true;
  return text === 'call you later' || text === 'will call later';
};

export const notifyNeedToCallChanged = (items) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('workloop:need-to-call-changed', {
    detail: { items: Array.isArray(items) ? items : undefined },
  }));
};

export const DEFAULT_QUICK_REPLIES = [
  'Thanks',
  'Yes',
  'No',
  'Okay',
  'Good morning',
  'Good night',
  'Sounds good',
  'Please wait',
  'On my way',
  'I’ll check',
  'Got it',
  'Noted',
];

const customQuickKey = (userId) => `workloop_quick_replies_${userId || 'anon'}`;

export const loadCustomQuickReplies = (userId) => {
  try {
    const raw = localStorage.getItem(customQuickKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map((item) => String(item || '').trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
};

export const saveCustomQuickReplies = (userId, replies) => {
  try {
    localStorage.setItem(customQuickKey(userId), JSON.stringify(replies));
  } catch {
    // ignore quota
  }
};
